import { useCallback, useEffect, useState } from 'react';
import type { IHeaderParams, IRowNode } from 'ag-grid-community';

/**
 * Column-header chevron that toggles every expandable row in the grid at once
 * (matches the Core Product header behaviour). Chevron-down when the grid has
 * any collapsed expandable row (click to expand all); chevron-up when every
 * expandable row is already expanded (click to collapse all).
 */
function ExpandAllHeader(props: IHeaderParams) {
  const { api } = props;

  const anyCollapsed = useCallback((): boolean => {
    if (!api) return true;
    let collapsed = false;
    api.forEachNode?.((node: IRowNode) => {
      if (collapsed) return;
      const isExpandable = Boolean(node.master) || Boolean(node.group);
      if (isExpandable && !node.expanded) collapsed = true;
    });
    return collapsed;
  }, [api]);

  const [showExpandGlyph, setShowExpandGlyph] = useState<boolean>(anyCollapsed());

  useEffect(() => {
    if (!api) return undefined;
    const refresh = () => setShowExpandGlyph(anyCollapsed());
    api.addEventListener?.('rowGroupOpened', refresh);
    api.addEventListener?.('modelUpdated', refresh);
    api.addEventListener?.('firstDataRendered', refresh);
    return () => {
      api.removeEventListener?.('rowGroupOpened', refresh);
      api.removeEventListener?.('modelUpdated', refresh);
      api.removeEventListener?.('firstDataRendered', refresh);
    };
  }, [api, anyCollapsed]);

  const handleClick = () => {
    if (!api) return;
    const expand = showExpandGlyph;
    api.forEachNode?.((node: IRowNode) => {
      const isExpandable = Boolean(node.master) || Boolean(node.group);
      if (isExpandable) node.setExpanded(expand);
    });
  };

  return (
    <div className="cag-header cag-header--expand-all">
      <button
        type="button"
        className="bombiix-icon-container"
        onClick={handleClick}
        aria-label={showExpandGlyph ? 'Expand all rows' : 'Collapse all rows'}
        title={showExpandGlyph ? 'Expand all rows' : 'Collapse all rows'}
      >
        <i className={showExpandGlyph ? 'bombiix-icon-chevron-down' : 'bombiix-icon-chevron-up'} aria-hidden="true" />
      </button>
    </div>
  );
}

export default ExpandAllHeader;
